<div class="w-full">
    <header class="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div class="min-w-0 flex-[1_1_18rem]">
            <h1 class="m-0 flex flex-wrap items-center gap-2.5 text-xl font-bold leading-7 text-slate-900">
                Atleti
                <span *ngIf="!loading" class="text-sm font-semibold text-slate-400">{{ counts.all ?? totalRecords }} totali</span>
                <span *ngIf="loading" class="h-4 w-16 animate-pulse rounded bg-slate-100"></span>
            </h1>
            <p class="m-0 mt-1 max-w-2xl text-sm leading-6 text-slate-500">
                Anagrafica degli atleti della società con categoria, pagamento e tesseramento
                <ng-container *ngIf="focusSeason">della stagione <b>{{ focusSeason.name }}</b></ng-container>.
            </p>
        </div>
        <div class="flex flex-wrap items-center gap-3">
            <button type="button" class="btn welldone-btn-gray inline-flex items-center gap-1.5"
                    [disabled]="loading || exporting || !totalRecords" (click)="exportCsv()">
                <i class="bi" [ngClass]="exporting ? 'bi-hourglass-split' : 'bi-download'" aria-hidden="true"></i>
                Esporta CSV
            </button>
            <button type="button" class="btn btn-primary inline-flex items-center gap-1.5" (click)="navigateToOnboarding()">
                <i class="bi bi-person-plus" aria-hidden="true"></i>
                Registra atleta
            </button>
        </div>
    </header>

    <!-- Filters -->
    <div class="mb-4 flex flex-wrap items-center gap-3">
        <input type="search" class="form-control form-control-solid !w-auto min-w-[12rem] flex-[1_1_18rem]"
               placeholder="Cerca nome, cognome o codice fiscale"
               [(ngModel)]="search" (ngModelChange)="onSearch($event)" (keyup.enter)="applyFilters()">
        <select class="form-select form-select-solid !w-auto min-w-[11rem]" aria-label="Stagione"
                [(ngModel)]="selectedSeasonId" (ngModelChange)="onSeasonChange()">
            <option [ngValue]="undefined">Tutte le stagioni</option>
            <option *ngFor="let season of seasons" [ngValue]="season.id">
                {{ season.name }}{{ season.isCurrent ? ' (corrente)' : '' }}
            </option>
        </select>
        <select class="form-select form-select-solid !w-auto min-w-[13rem]" aria-label="Categoria"
                [(ngModel)]="selectedCategoryId" (ngModelChange)="applyFilters()" [disabled]="!categoryOptions.length">
            <option [ngValue]="undefined">Tutte le categorie</option>
            <option *ngFor="let category of categoryOptions" [ngValue]="category.id">{{ category.name }}</option>
        </select>
        <button *ngIf="hasActiveFilters" type="button" class="btn btn-reset-link" (click)="resetFilters()">Azzera</button>
    </div>

    <div class="mb-5 flex flex-wrap gap-2" role="group" aria-label="Filtri rapidi">
        <button *ngFor="let filter of quickFilters" type="button"
                class="inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-sm font-semibold transition"
                [ngClass]="quickFilter === filter ? 'border-primary bg-primary-soft text-primary' : 'border-slate-200 bg-white text-slate-600 hover:border-slate-300'"
                [attr.aria-pressed]="quickFilter === filter" (click)="setQuickFilter(filter)">
            <i *ngIf="filter === 'missingPayment'" class="h-2 w-2 rounded-full bg-red-500"></i>
            <i *ngIf="filter === 'membership'" class="h-2 w-2 rounded-full bg-emerald-500"></i>
            <i *ngIf="filter === 'expiredDocument'" class="h-2 w-2 rounded-full bg-amber-500"></i>
            {{ quickFilterLabel(filter) }}
            <span class="tabular-nums opacity-70">{{ counts[filter] ?? '…' }}</span>
        </button>
    </div>

    <!-- Athletes -->
    <div class="overflow-hidden rounded-3xl border border-slate-100 bg-white shadow-sm">
        <div class="overflow-x-auto">
            <table class="w-full border-collapse text-sm">
                <thead>
                    <tr class="border-b-2 border-slate-100 bg-slate-50">
                        <th class="whitespace-nowrap px-4 py-4 text-left text-xs font-bold uppercase tracking-wide !text-slate-500">Atleta</th>
                        <th class="whitespace-nowrap px-4 py-4 text-left text-xs font-bold uppercase tracking-wide !text-slate-500">Contatti</th>
                        <th class="whitespace-nowrap px-4 py-4 text-left text-xs font-bold uppercase tracking-wide !text-slate-500">
                            Categoria <span *ngIf="focusSeason" class="font-semibold normal-case tracking-normal text-slate-400">{{ focusSeason.name }}</span>
                        </th>
                        <th class="whitespace-nowrap px-4 py-4 text-left text-xs font-bold uppercase tracking-wide !text-slate-500">Pagamento</th>
                        <th class="whitespace-nowrap px-4 py-4 text-left text-xs font-bold uppercase tracking-wide !text-slate-500">Tesseramento</th>
                        <th *ngIf="primaryDocumentExists" class="whitespace-nowrap px-4 py-4 text-left text-xs font-bold uppercase tracking-wide !text-slate-500">Documento</th>
                        <th class="sticky right-0 w-px bg-slate-50 px-4 py-4"><span class="sr-only">Azioni</span></th>
                    </tr>
                </thead>

                <tbody *ngIf="loading">
                    <tr *ngFor="let row of skeletonRows" class="border-b border-slate-100 last:border-0">
                        <td class="px-4 py-4">
                            <div class="flex items-center gap-3">
                                <span class="h-10 w-10 shrink-0 animate-pulse rounded-full bg-slate-100"></span>
                                <div class="flex flex-col gap-1.5">
                                    <span class="h-3.5 w-32 animate-pulse rounded bg-slate-100"></span>
                                    <span class="h-3 w-24 animate-pulse rounded bg-slate-100"></span>
                                </div>
                            </div>
                        </td>
                        <td class="px-4 py-4"><span class="block h-3.5 w-40 animate-pulse rounded bg-slate-100"></span></td>
                        <td class="px-4 py-4"><span class="block h-3.5 w-32 animate-pulse rounded bg-slate-100"></span></td>
                        <td class="px-4 py-4"><span class="block h-6 w-24 animate-pulse rounded-full bg-slate-100"></span></td>
                        <td class="px-4 py-4"><span class="block h-3.5 w-28 animate-pulse rounded bg-slate-100"></span></td>
                        <td *ngIf="primaryDocumentExists" class="px-4 py-4"><span class="block h-3.5 w-28 animate-pulse rounded bg-slate-100"></span></td>
                        <td class="sticky right-0 bg-white px-4 py-4"><span class="block h-9 w-[4.75rem] animate-pulse rounded-xl bg-slate-100"></span></td>
                    </tr>
                </tbody>

                <tbody *ngIf="!loading">
                    <tr *ngFor="let athlete of athletes; trackBy: trackById"
                        class="group border-b border-slate-100 transition-colors last:border-0 hover:bg-slate-50">
                        <td class="px-4 py-3.5">
                            <div class="flex min-w-0 items-center gap-3">
                                <app-identity-avatar
                                    [imageUrl]="athlete.image"
                                    [initials]="initials(athlete)"
                                    [colorKey]="athlete.id || ''"
                                ></app-identity-avatar>
                                <div class="min-w-0">
                                    <a class="block truncate font-semibold text-slate-900 no-underline hover:text-primary"
                                       [routerLink]="['/companies/athlete', athlete.id]">
                                        {{ athlete.lastName }} {{ athlete.firstName }}
                                    </a>
                                    <span class="flex items-center gap-1.5">
                                        <span class="truncate font-mono text-xs uppercase text-slate-400">{{ athlete.fiscalCode || '—' }}</span>
                                        <span *ngIf="athlete.status === 'pendingCompanyApprovation'"
                                              class="whitespace-nowrap rounded-full bg-amber-100 px-1.5 py-px text-[0.65rem] font-bold text-amber-800">Da approvare</span>
                                    </span>
                                </div>
                            </div>
                        </td>
                        <td class="px-4 py-3.5">
                            <div class="flex flex-col gap-0.5 text-[0.82rem]">
                                <a *ngIf="athlete.phone" class="inline-flex items-center gap-1.5 whitespace-nowrap text-slate-700 no-underline hover:text-primary"
                                   [href]="'tel:' + athlete.phone">
                                    <i class="bi bi-telephone text-slate-400" aria-hidden="true"></i>{{ athlete.phone }}
                                </a>
                                <a *ngIf="athlete.email" class="inline-flex max-w-[13rem] items-center gap-1.5 truncate text-slate-700 no-underline hover:text-primary"
                                   [href]="'mailto:' + athlete.email">
                                    <i class="bi bi-envelope text-slate-400" aria-hidden="true"></i><span class="truncate">{{ athlete.email }}</span>
                                </a>
                                <span *ngIf="!athlete.phone && !athlete.email" class="text-slate-400">—</span>
                            </div>
                        </td>

                        <ng-container *ngIf="focusEnrollment(athlete) as enrollment; else notEnrolled">
                            <td class="px-4 py-3.5">
                                <a class="block max-w-[12rem] truncate text-[0.82rem] font-semibold text-slate-700 no-underline hover:text-primary"
                                   [title]="categoryLabel(enrollment.categoryId)"
                                   [routerLink]="['/companies/subscriptions/category', enrollment.categoryId]">
                                    {{ categoryLabel(enrollment.categoryId) || '—' }}
                                </a>
                            </td>
                            <td class="px-4 py-3.5">
                                <span class="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-bold ring-1 ring-inset"
                                      [ngClass]="statusClasses(displayStatus(enrollment))">
                                    <i class="bi" [ngClass]="statusIcon(displayStatus(enrollment))" aria-hidden="true"></i>
                                    {{ statusLabel(displayStatus(enrollment)) }}
                                </span>
                                <span class="mt-1 flex items-center gap-1 text-xs tabular-nums text-slate-500"><i class="bi bi-wallet2 text-slate-400" aria-hidden="true"></i>{{ formatEuro(amount(enrollment)) }}</span>
                            </td>
                        </ng-container>
                        <ng-template #notEnrolled>
                            <td class="px-4 py-3.5" colspan="2">
                                <span class="text-[0.82rem] text-slate-400">
                                    Non iscritto<ng-container *ngIf="focusSeason"> alla stagione {{ focusSeason.name }}</ng-container>
                                </span>
                            </td>
                        </ng-template>

                        <td class="px-4 py-3.5">
                            <span class="inline-flex items-center gap-1.5 whitespace-nowrap text-[0.82rem] font-semibold"
                                  [ngClass]="hasMembership(athlete) ? 'text-emerald-700' : 'text-red-600'">
                                <i class="bi" [ngClass]="hasMembership(athlete) ? 'bi-patch-check-fill' : 'bi-patch-exclamation'" aria-hidden="true"></i>
                                {{ hasMembership(athlete) ? 'Tesserato' : 'Non tesserato' }}
                            </span>
                            <span class="mt-0.5 block whitespace-nowrap text-xs text-slate-400">{{ membershipText(athlete) }}</span>
                        </td>
                        <td *ngIf="primaryDocumentExists" class="px-4 py-3.5">
                            <span class="whitespace-nowrap text-[0.82rem] font-semibold"
                                  [ngClass]="documentClasses(documentState(athlete).status)">
                                {{ documentState(athlete).text }}
                            </span>
                        </td>
                        <td class="sticky right-0 bg-white px-4 py-3.5 shadow-[-10px_0_12px_-12px_rgba(15,23,42,0.25)] transition-colors group-hover:bg-slate-50">
                            <div class="flex justify-end gap-2">
                                <a class="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 bg-slate-100 text-slate-600 no-underline transition hover:border-primary hover:bg-primary-soft hover:text-primary"
                                   title="Apri scheda atleta" aria-label="Apri scheda atleta"
                                   [routerLink]="['/companies/athlete', athlete.id]">
                                    <i class="bi bi-arrow-right" aria-hidden="true"></i>
                                </a>
                                <button type="button"
                                        class="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-red-200 bg-red-50 text-red-600 transition hover:border-red-600 hover:bg-red-600 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-300"
                                        title="Elimina atleta" aria-label="Elimina atleta" (click)="deleteAthlete(athlete)">
                                    <i class="bi bi-trash" aria-hidden="true"></i>
                                </button>
                            </div>
                        </td>
                    </tr>

                    <tr *ngIf="!athletes.length">
                        <td [attr.colspan]="primaryDocumentExists ? 7 : 6" class="px-5 py-12 text-center">
                            <ng-container *ngIf="loadError; else emptyState">
                                <i class="bi bi-exclamation-triangle mb-2 block text-3xl text-amber-500" aria-hidden="true"></i>
                                <p class="m-0 font-semibold text-slate-700">Impossibile caricare gli atleti</p>
                                <button type="button" class="btn btn-reset-link mt-2" (click)="load()">Riprova</button>
                            </ng-container>
                            <ng-template #emptyState>
                                <ng-container *ngIf="hasActiveFilters; else noAthletes">
                                    <p class="m-0 text-slate-500">Nessun atleta corrisponde ai filtri.</p>
                                    <button type="button" class="btn btn-reset-link mt-2" (click)="resetFilters()">Azzera filtri</button>
                                </ng-container>
                                <ng-template #noAthletes>
                                    <i class="bi bi-people mb-2 block text-3xl text-slate-300" aria-hidden="true"></i>
                                    <p class="m-0 font-semibold text-slate-700">Ancora nessun atleta</p>
                                    <p class="m-0 mt-1 text-sm text-slate-400">Registra il primo atleta o condividi il link di iscrizione della società.</p>
                                </ng-template>
                            </ng-template>
                        </td>
                    </tr>
                </tbody>
            </table>
        </div>
    </div>

    <!-- Pagination -->
    <div *ngIf="totalRecords > 0" class="mt-5 flex flex-wrap items-center justify-between gap-3">
        <span class="text-sm font-semibold text-slate-500">{{ rangeLabel }}</span>
        <div class="flex items-center gap-1.5">
            <button type="button"
                    class="inline-flex h-9 w-9 items-center justify-center rounded-xl border-0 bg-slate-100 text-slate-600 transition hover:bg-slate-200 disabled:opacity-40"
                    aria-label="Pagina precedente" [disabled]="loading || page === 0" (click)="goToPage(page - 1)">
                <i class="bi bi-chevron-left" aria-hidden="true"></i>
            </button>
            <span class="px-2 text-sm font-semibold tabular-nums text-slate-600">{{ page + 1 }} / {{ pageCount }}</span>
            <button type="button"
                    class="inline-flex h-9 w-9 items-center justify-center rounded-xl border-0 bg-slate-100 text-slate-600 transition hover:bg-slate-200 disabled:opacity-40"
                    aria-label="Pagina successiva" [disabled]="loading || page >= pageCount - 1" (click)="goToPage(page + 1)">
                <i class="bi bi-chevron-right" aria-hidden="true"></i>
            </button>
        </div>
        <label class="m-0 inline-flex items-center gap-2 text-sm text-slate-500">
            Righe
            <select class="rounded-xl border-0 bg-slate-100 px-3 py-1.5 text-sm font-semibold text-slate-600"
                    [(ngModel)]="pageSize" (ngModelChange)="onPageSizeChange()">
                <option *ngFor="let size of pageSizes" [ngValue]="size">{{ size }}</option>
            </select>
        </label>
    </div>
</div>
